import React, { useState, useEffect } from 'react';
import TomarDatosMenoresForm from './TomarDatosMenoresForm';
import { getEntradaService, getMediasService, getTiposPagoService } from '../services/EntradaService';
import { generarPdfContrato } from "../utils/generadorContratoPDF";
import { tomarEntradaSchema } from '../utils/validation';
import FieldError from '../utils/FieldError';

const safeFloat = v => Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0;
const safeInt = v => Number.isFinite(parseInt(v)) ? parseInt(v) : 0;

const TomarEntradaForm = ({ onFormComplete, initialFormData }) => {
    const [form, setForm] = useState({
        nombreAdultoResponsable: '',
        dniAdultoResponsable: '',
        correoAdultoResponsable: '',
        tipoPago: '',
        aceptaCondiciones: false,
        ingresanMenores: false
    });

    const [datosMenores, setDatosMenores] = useState([]);
    const [precioBase, setPrecioBase] = useState(1);
    const [cantidadMedias, setCantidadMedias] = useState(0);
    const [duracionHoras, setDuracionHoras] = useState(1);

    const [entradasDisponibles, setEntradasDisponibles] = useState([]);
    const [precioMedias, setPrecioMedias] = useState(0);
    const [tiposPago, setTiposPago] = useState([]);
    const [entradasSeleccionadas, setEntradasSeleccionadas] = useState([]);
    const [mostrarPopup, setMostrarPopup] = useState(false);

    const [totalEditable, setTotalEditable] = useState(0);
    const [totalEditadoManualmente, setTotalEditadoManualmente] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const entradas = await getEntradaService();
                setEntradasDisponibles(Array.isArray(entradas) ? entradas : []);
                const medias = await getMediasService();
                setPrecioMedias(safeFloat(medias?.precio ?? medias?.precioUnitario ?? 0));
                const tipos = await getTiposPagoService();
                setTiposPago(Array.isArray(tipos) ? tipos : []);
                if (Array.isArray(entradas) && entradas.length > 0 && precioBase === 1) {
                    setPrecioBase(safeFloat(entradas[0].precio));
                }
            } catch (err) { console.error(err); }
        };
        fetchAll();
    }, []);

    useEffect(() => {
        if (initialFormData) {
            setForm({
                nombreAdultoResponsable: initialFormData.nombreAdultoResponsable || '',
                dniAdultoResponsable: initialFormData.dniAdultoResponsable || '',
                correoAdultoResponsable: initialFormData.correoAdultoResponsable || '',
                tipoPago: initialFormData.tipoPago || '',
                aceptaCondiciones: initialFormData.aceptaCondiciones || false,
                ingresanMenores: initialFormData.ingresanMenores || false
            });
            setDatosMenores(initialFormData.menores || []);
            setCantidadMedias(initialFormData.cantidadMedias || 0);
            setDuracionHoras(initialFormData.duracionHoras || 1);
            if (initialFormData.entradasSeleccionadas) {
                setEntradasSeleccionadas(initialFormData.entradasSeleccionadas.map(e => ({ ...e, cantidad: e.cantidad ?? 1 })));
            }
            if (initialFormData.totalPagar != null) {
                setTotalEditable(safeFloat(initialFormData.totalPagar));
                setTotalEditadoManualmente(true);
            }
        }
    }, [initialFormData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleMenoresChange = (nuevosDatos) => setDatosMenores(nuevosDatos);

    const handleAgregarEntrada = (entrada) => {
        if (!entrada) return;
        setEntradasSeleccionadas(prev => {
            const existe = prev.find(e => e.id === entrada.id);
            if (existe) return prev.map(e => e.id === entrada.id ? ({ ...e, cantidad: (safeInt(e.cantidad) || 1) + 1 }) : e);
            return [...prev, { ...entrada, cantidad: 1 }];
        });
        setMostrarPopup(false);
        setTotalEditadoManualmente(false);
    };

    const handleQuitarEntrada = (id) => {
        setEntradasSeleccionadas(prev => prev.filter(e => e.id !== id));
        setTotalEditadoManualmente(false);
    };

    const handleCambiarCantidad = (id, nuevaCantidad) => {
        const q = Math.max(1, safeInt(nuevaCantidad));
        setEntradasSeleccionadas(prev => prev.map(e => e.id === id ? { ...e, cantidad: q } : e));
        setTotalEditadoManualmente(false);
    };

    const cantidadPersonasCalculada = entradasSeleccionadas.reduce((acc, e) => acc + safeInt(e.cantidad), 0);
    const cantidadTickets = entradasSeleccionadas.reduce((acc, e) => acc + safeInt(e.cantidadEntradas) * safeInt(e.cantidad), 0);
    const subtotalMedias = safeInt(cantidadMedias) * safeFloat(precioMedias);
    const totalCalculado = entradasSeleccionadas.reduce((acc, e) => acc + safeFloat(e.precio) * safeInt(e.cantidad), 0) + subtotalMedias;

    useEffect(() => { if (!totalEditadoManualmente) setTotalEditable(Number(totalCalculado.toFixed(2))); }, [totalCalculado, totalEditadoManualmente]);

    const onTotalEditableChange = (val) => { setTotalEditable(safeFloat(val)); setTotalEditadoManualmente(true); };
    const resetearTotalAutomatico = () => { setTotalEditable(Number(totalCalculado.toFixed(2))); setTotalEditadoManualmente(false); };

    const esFormularioValido = () => form.nombreAdultoResponsable.trim() && form.dniAdultoResponsable.trim() && form.correoAdultoResponsable.includes('@') && form.tipoPago && cantidadPersonasCalculada > 0 && duracionHoras > 0;

    const handleSubmit = () => {
        const result = tomarEntradaSchema.safeParse(form);
        if (!result.success) {
            const fieldErrors = {};
            result.error.issues.forEach((i) => { fieldErrors[i.path[0]] = i.message; });
            setErrors(fieldErrors);
            return;
        }
        const payload = {
            ...result.data,
            menores: form.ingresanMenores ? datosMenores : [],
            cantidadTotalPersonas: cantidadPersonasCalculada,
            cantidadMedias: safeInt(cantidadMedias),
            duracionHoras: safeInt(duracionHoras),
            entradasSeleccionadas: entradasSeleccionadas.map(({ id, ...rest }) => rest),
            cantidadTickets,
            totalPagar: safeFloat(totalEditable),
            cantidadImprimir: cantidadTickets,
            duracion: safeInt(duracionHoras)
        };
        onFormComplete?.(payload);
    };

    return (
        <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-5">
            <div className="space-y-4">
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Nombre adulto responsable:</label>
                    <input type="text" name="nombreAdultoResponsable" value={form.nombreAdultoResponsable} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    <FieldError message={errors.nombreAdultoResponsable} />
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">DNI:</label>
                    <input type="text" name="dniAdultoResponsable" value={form.dniAdultoResponsable} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    <FieldError message={errors.dniAdultoResponsable} />
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Email:</label>
                    <input type="email" name="correoAdultoResponsable" value={form.correoAdultoResponsable} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    <FieldError message={errors.correoAdultoResponsable} />
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Tipo de pago:</label>
                    <select name="tipoPago" value={form.tipoPago} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="">-- Seleccionar --</option>
                        {tiposPago.map((tp, idx) => <option key={idx} value={tp.id ?? tp}>{tp.nombre ?? tp}</option>)}
                    </select>
                    <FieldError message={errors.tipoPago} />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" name="ingresanMenores" checked={form.ingresanMenores} onChange={handleChange} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    ¿Ingresan menores?
                </label>
                {form.ingresanMenores && <TomarDatosMenoresForm onChange={handleMenoresChange} initialData={datosMenores} />}

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">Duración (horas):</label>
                        <input type="number" min="1" value={duracionHoras} onChange={(e) => setDuracionHoras(safeInt(e.target.value) || 1)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">Cantidad de medias:</label>
                        <input type="number" min="0" value={cantidadMedias} onChange={(e) => setCantidadMedias(safeInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                        <p className="text-xs text-gray-400">Precio unitario medias: ${Number(precioMedias).toFixed(2)}</p>
                    </div>
                </div>

                <button type="button" onClick={() => setMostrarPopup(true)} className="w-full py-2.5 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors">
                    + Agregar entrada
                </button>

                {entradasSeleccionadas.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {Object.keys(entradasSeleccionadas[0]).filter(k => k !== 'id').map(k => (
                                        <th key={k} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{k}</th>
                                    ))}
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {entradasSeleccionadas.map(e => (
                                    <tr key={e.id} className="hover:bg-gray-50">
                                        {Object.keys(e).filter(k => k !== 'id' && k !== 'cantidad').map(k => (
                                            <td key={k} className="px-3 py-2 text-sm text-gray-700">{e[k]}</td>
                                        ))}
                                        <td className="px-3 py-2 text-sm text-gray-700">
                                            <input type="number" min="1" value={e.cantidad} onChange={(ev) => handleCambiarCantidad(e.id, ev.target.value)} className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500" />
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-700">${(safeFloat(e.precio) * safeInt(e.cantidad)).toFixed(2)}</td>
                                        <td className="px-3 py-2">
                                            <button type="button" onClick={() => handleQuitarEntrada(e.id)} className="px-2 py-1 bg-danger-600 text-white text-xs rounded hover:bg-danger-700 transition-colors">Quitar</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md space-y-1">
                            <p className="text-sm text-gray-600">Subtotal entradas: <strong className="text-gray-900">${entradasSeleccionadas.reduce((acc, e) => acc + safeFloat(e.precio) * safeInt(e.cantidad), 0).toFixed(2)}</strong></p>
                            <p className="text-sm text-gray-600">Subtotal medias: <strong className="text-gray-900">${subtotalMedias.toFixed(2)}</strong></p>
                            <p className="text-sm text-gray-600">Cantidad de tickets a imprimir: <strong className="text-gray-900">{cantidadTickets}</strong></p>
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600">Total (editable):</label>
                                <input type="number" value={totalEditable} onChange={(e) => onTotalEditableChange(e.target.value)} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" step="0.01" />
                                <button type="button" onClick={resetearTotalAutomatico} className="px-2 py-1 text-xs text-primary-600 hover:text-primary-700">Reset total calculado</button>
                            </div>
                        </div>
                    </div>
                )}

                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" name="aceptaCondiciones" checked={form.aceptaCondiciones} onChange={handleChange} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    Acepto términos y condiciones
                </label>
                <FieldError message={errors.aceptaCondiciones} />

                <div className="flex gap-3">
                    <button type="button" disabled={!esFormularioValido()} onClick={() =>
                        generarPdfContrato({ ...form, menores: form.ingresanMenores ? datosMenores : [], cantidadTotalPersonas: cantidadPersonasCalculada, cantidadMedias: safeInt(cantidadMedias), duracionHoras: safeInt(duracionHoras), entradasSeleccionadas, cantidadTickets, totalPagar: safeFloat(totalEditable) })
                    } className="flex-1 py-2.5 px-4 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Descargar contrato PDF</button>

                    <button type="button" disabled={!form.aceptaCondiciones || !esFormularioValido()} onClick={handleSubmit} className="flex-1 py-2.5 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Continuar</button>
                </div>
            </div>

            {mostrarPopup && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Entradas disponibles</h3>
                        <div className="overflow-x-auto max-h-80 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {entradasDisponibles.length > 0 && Object.keys(entradasDisponibles[0]).filter(k => k !== 'id').map(k => (
                                            <th key={k} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{k}</th>
                                        ))}
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {entradasDisponibles.map(e => (
                                        <tr key={e.id} className="hover:bg-gray-50">
                                            {Object.keys(e).filter(k => k !== 'id').map(k => (
                                                <td key={k} className="px-3 py-2 text-sm text-gray-700">{e[k]}</td>
                                            ))}
                                            <td className="px-3 py-2">
                                                <button type="button" onClick={() => handleAgregarEntrada(e)} className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition-colors">Elegir</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button type="button" onClick={() => setMostrarPopup(false)} className="mt-4 px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors">Cerrar</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TomarEntradaForm;
